import React, { Fragment, memo } from 'react';
import { motion } from 'framer-motion';
import { SlideLayout } from '../SlideLayout';
import { ArrowRight, Cpu } from 'lucide-react';
const fpgaSteps = [{
  name: 'LayerNorm',
  color: 'bg-brand-primary/40'
}, {
  name: 'Attention',
  color: 'bg-brand-primary/50'
}, {
  name: 'Softmax',
  color: 'bg-brand-primary/40'
}, {
  name: 'RoPE',
  color: 'bg-brand-primary/50'
}, {
  name: 'SiLU',
  color: 'bg-brand-primary/40'
}, {
  name: 'Quantize',
  color: 'bg-brand-primary/50'
}];
const gpuBullets = ['Kernel fusion reduces round-trips to HBM', 'Still temporal: one fused kernel executes at a time', 'Driver scheduling and kernel launch overhead remain', 'Limited to fusion patterns the compiler supports'];
const fpgaBullets = [{
  label: 'Spatial parallelism',
  desc: 'multiple pipeline stages execute simultaneously across the chip'
}, {
  label: 'Arbitrary fusion',
  desc: 'not limited to compiler-recognized patterns — wire exactly the dataflow you want'
}, {
  label: 'Zero driver overhead',
  desc: 'no kernel launches, no scheduling latency, deterministic cycle-by-cycle execution'
}, {
  label: 'Custom operators',
  desc: "implement sparse patterns, custom quantization, or MoE routing that don't map cleanly to GPU primitives"
}];
export function DataflowFusionSlide() {
  return <SlideLayout slideNumber={12} totalSlides={20} title="Operator Fusion" data-id="element-104">
      <div className="w-full h-full flex flex-col gap-4" data-id="element-105">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center" data-id="element-106">
          <h2 className="text-4xl md:text-5xl font-bold mb-2" data-id="element-107">
            Custom <span className="text-brand-primary" data-id="element-108">Dataflow</span> Pipeline
          </h2>
          <p className="text-xl text-brand-muted max-w-4xl mx-auto" data-id="element-109">
            Modern GPUs use kernel fusion (FlashAttention, vLLM PagedAttention)
            to reduce memory traffic. FPGAs take this further with{' '}
            <span className="text-white font-semibold" data-id="element-110">
              spatially distributed pipelines
            </span>{' '}
            that wire operators directly together at the hardware level.
          </p>
        </motion.div>

        {/* Two-column comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1" data-id="element-111">
          {/* GPU */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="bg-brand-surface border border-brand-border rounded-2xl p-5 flex flex-col" data-id="element-112">
            <div className="flex items-center gap-2 mb-3" data-id="element-113">
              <Cpu size={18} className="text-chip-gpu" data-id="element-114" />
              <h3 className="text-lg font-bold text-chip-gpu" data-id="element-115">
                GPU: Fused Kernels (State of the Art)
              </h3>
            </div>
            {/* ASCII-style diagram */}
            <div className="bg-brand-dark rounded-lg p-3 font-mono text-sm mb-3 border border-chip-gpu/20" data-id="element-116">
              <div className="text-chip-gpu/60 text-xs mb-1" data-id="element-117">
                FlashAttention example
              </div>
              <div className="text-white" data-id="element-118">Q×K → Softmax → ×V</div>
              <div className="text-brand-muted text-xs mt-1" data-id="element-119">
                (stays in SRAM, minimizes HBM)
              </div>
            </div>
            <ul className="space-y-2 flex-1" data-id="element-120">
              {gpuBullets.map((b, i) => <li key={i} className="flex items-start gap-2 text-base text-brand-muted" data-id="element-121">
                  <span className="text-chip-gpu mt-1 shrink-0" data-id="element-122">•</span>
                  <span data-id="element-123">{b}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* FPGA */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-brand-surface border border-brand-primary/30 rounded-2xl p-5 relative overflow-hidden flex flex-col" data-id="element-124">
            <div className="absolute top-0 left-0 w-full h-1 bg-brand-primary" data-id="element-125" />
            <div className="flex items-center gap-2 mb-3" data-id="element-126">
              <Cpu size={18} className="text-brand-primary" data-id="element-127" />
              <h3 className="text-lg font-bold text-brand-primary" data-id="element-128">
                FPGA: Spatial Hardware Pipeline
              </h3>
            </div>
            {/* Pipeline diagram */}
            <div className="bg-brand-dark rounded-lg p-3 mb-3 border border-brand-primary/20" data-id="element-129">
              <div className="flex items-center gap-0 overflow-x-auto pb-1" data-id="element-130">
                {fpgaSteps.map((step, i) => <Fragment key={step.name} data-id="element-131">
                    <motion.div initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: i * 0.08 + 0.5
                }} className={`${step.color} border-y border-brand-primary/20 ${i === 0 ? 'border-l rounded-l-lg' : ''} ${i === fpgaSteps.length - 1 ? 'border-r rounded-r-lg' : ''} px-3 py-2 text-center shrink-0`} data-id="element-132">
                      <div className="text-xs font-mono text-white whitespace-nowrap" data-id="element-133">
                        {step.name}
                      </div>
                    </motion.div>
                    {i < fpgaSteps.length - 1 && <div className="shrink-0 text-brand-primary" data-id="element-134">
                        <ArrowRight size={12} data-id="element-135" />
                      </div>}
                  </Fragment>)}
              </div>
              <div className="text-xs text-brand-primary/70 font-mono mt-2" data-id="element-136">
                Direct wiring — output of each stage feeds next with no memory
                hop
              </div>
            </div>
            <ul className="space-y-2 flex-1" data-id="element-137">
              {fpgaBullets.map((b, i) => <li key={i} className="flex items-start gap-2 text-base text-brand-muted" data-id="element-138">
                  <span className="text-brand-primary mt-1 shrink-0" data-id="element-139">•</span>
                  <span data-id="element-140">
                    <span className="text-white font-semibold" data-id="element-141">{b.label}:</span>{' '}
                    {b.desc}
                  </span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* FlightLLM + Advantage callout */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-142">
          <div className="bg-brand-dark border border-brand-border rounded-xl p-4" data-id="element-143">
            <h4 className="text-base font-mono text-brand-muted uppercase tracking-wider mb-2" data-id="element-144">
              Case Study: FlightLLM
            </h4>
            <p className="text-base text-brand-muted" data-id="element-145">
              Alveo U280 FPGA uses{' '}
              <span className="text-white font-semibold" data-id="element-146">
                sparse DSP chains
              </span>{' '}
              and keeps token embeddings in{' '}
              <span className="text-white font-semibold" data-id="element-147">on-chip URAM</span> to
              eliminate re-fetching across decode steps — a custom dataflow
              optimization difficult to replicate in a GPU's temporal execution
              model.
            </p>
            <p className="text-xs text-brand-muted mt-2 opacity-60" data-id="element-148">
              Sources: [26][27]
            </p>
          </div>
          <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-xl p-4 flex items-center" data-id="element-149">
            <p className="text-base text-white leading-relaxed" data-id="element-150">
              <span className="text-brand-primary font-bold" data-id="element-151">
                The Advantage:
              </span>{' '}
              For models with non-standard operators or latency-critical
              pipelines, FPGAs offer{' '}
              <span className="text-brand-primary font-semibold" data-id="element-152">
                deterministic, fully custom dataflows
              </span>{' '}
              that bypass software stack limitations entirely.
            </p>
          </div>
        </motion.div>
      </div>
    </SlideLayout>;
}